import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card } from "@rumbledirect/ui";
import { Form, message } from "antd";
import { useAffiliate } from "@/lib/hooks/useAffiliate";
import {
  assignRootServicePartners,
  fetchRootServicePartnersByAffiliateId,
} from "@/lib/api/resources/servicePartners";
import { ServicepartnerRootImportTableTransfer } from "./ServicepartnerRootImportTableTransfer";
import { servicepartnerTransferColumns } from "@/pages/admin/servicepartners/lib/constants/servicepartnerTransferColumns";

export interface ServicePartnerRootImportAssignValues {
  assignServicePartners: { servicePartnerId: string; brandId?: string }[];
  removeServicePartnerIds: string[];
}

interface ServicePartnerRootImportFormValues {
  selectedServicePartners: string[];
}

export const ServicePartnerRootImportForm = () => {
  const [form] = Form.useForm();
  const { currentAffiliate } = useAffiliate();
  const queryClient = useQueryClient();

  const servicePartnersQuery = useQuery({
    queryKey: ["root-servicePartners", currentAffiliate],
    queryFn: async () =>
      fetchRootServicePartnersByAffiliateId(currentAffiliate!),
    enabled: !!currentAffiliate,
    select: (data) => {
      const allServicePartners = [
        ...data.assignedServicePartners,
        ...data.unassignedServicePartners,
      ];

      return {
        allServicePartners:
          allServicePartners?.map((sp) => ({ ...sp, key: sp.id })) ?? [],
        assignedServicePartners:
          data.assignedServicePartners?.map((sp) => sp.id) ?? [],
      };
    },
  });

  const handleSubmit = (values: ServicePartnerRootImportFormValues) => {
    const selectedServicePartnerIds = values.selectedServicePartners || [];
    // Without a brandId the backend assigns the service partner's default brand.
    const assignServicePartners = selectedServicePartnerIds
      .filter(
        (id) =>
          !servicePartnersQuery.data?.assignedServicePartners.includes(id),
      )
      .map((servicePartnerId) => ({ servicePartnerId }));
    const removeServicePartnerIds =
      servicePartnersQuery.data?.assignedServicePartners.filter(
        (id) => !selectedServicePartnerIds.includes(id),
      ) || [];
    assignServicePartnersMutation.mutate({
      assignServicePartners,
      removeServicePartnerIds,
    });
  };

  const assignServicePartnersMutation = useMutation({
    mutationFn: async (data: ServicePartnerRootImportAssignValues) =>
      assignRootServicePartners(currentAffiliate!, data),
    onSuccess: () => {
      message.success("Herstellers succesvol toegewezen");
      queryClient.invalidateQueries({
        queryKey: ["root-servicePartners", currentAffiliate],
      });
    },
    onError: () => {
      message.error("Er is iets misgegaan bij het toewijzen van herstellers");
    },
  });

  return (
    <Card
      title="Importeer herstellers uit stamgegevens"
      loading={servicePartnersQuery.isPending}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          valuePropName="targetKeys"
          name="selectedServicePartners"
          initialValue={
            servicePartnersQuery.data?.assignedServicePartners ?? []
          }
        >
          <ServicepartnerRootImportTableTransfer
            titles={["Beschikbare herstellers", "Gekoppelde herstellers"]}
            columns={servicepartnerTransferColumns}
            dataSource={servicePartnersQuery.data?.allServicePartners ?? []}
          />
        </Form.Item>
        <Form.Item label={null}>
          <Button
            type="primary"
            htmlType="submit"
            loading={assignServicePartnersMutation.isPending}
          >
            Opslaan
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
